import { useMemo } from 'react';

import { inertPreferencesFor } from '../format/passthrough';
import {
  type Dialect,
  type ServerParams,
  toServerParams,
} from '../server/dialect';
import { useLocalizationContext } from './useLocalizationContext';

/**
 * The parameters one endpoint needs to render the user's preferences server-side.
 *
 * For requests whose output the backend formats — CSV exports, generated reports — where sending
 * nothing means accepting a default that differs from what the screen is showing.
 *
 * The result keeps the same identity for as long as the preferences hold, so it is safe in a
 * dependency array and will not re-trigger a query on every render. That relies on the dialect
 * being a module-level constant, which is how the registry declares them.
 *
 * With the rollout flag off it speaks for the same reader the screen is rendering for, which off
 * means the en-US one. That is the point: the screen converts to miles while the flag is off, so a
 * CSV generated from a request that said nothing would come back in whatever the server defaults
 * to and disagree with the table beside it. The dialects exist to prevent exactly that
 * disagreement, and they cannot do it by staying silent on one side of the flag.
 *
 * @param dialect - The endpoint's declared vocabulary. See {@link Dialect}.
 * @returns One property per declared parameter, ready to spread into the request.
 * @throws If no `<LocalizationProvider>` is mounted above the caller.
 */
export const useServerParams = <
  TArg,
  TKeys extends Extract<keyof TArg, string>,
>(
  dialect: Dialect<TArg, TKeys>
): ServerParams<TArg, TKeys> => {
  const { preferences, enabled } = useLocalizationContext();

  return useMemo(
    () =>
      toServerParams(
        dialect,
        enabled ? preferences : inertPreferencesFor(preferences)
      ),
    [dialect, preferences, enabled]
  );
};
